import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import type { ICommand } from '../../../core/commands/ICommand';
import type {
  CommandDeltaContribution,
  IScenarioRecordable,
} from '../../../core/data-profiles/scenarios/ScenarioRecordable';
import {
  createEdgeId,
  createNodeId,
  type EdgeAddition,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';

/**
 * CreateNetworkConnectorCommand — creates a network connector
 * (containment / sg_access / data_flow / role_assumption / routing /
 * governance) and participates in scenario recording.
 *
 * Wraps the shared `CreateEdgeCommand` with `category: 'network'`. In
 * scenario editing mode the edge becomes an `EdgeAddition` in the
 * delta instead of landing on the live diagram.
 */
export interface CreateNetworkConnectorParams {
  id?: string;
  type: string;
  from: string;
  to: string;
  /** Edge-level anchor side (top/right/bottom/left). When set, the
   *  canvas's live router uses these instead of inferring per frame.
   *  Used by compose_service to stamp the router's per-edge analysis
   *  while keeping the polyline live (drag-responsive). */
  fromAnchor?: 'top' | 'right' | 'bottom' | 'left';
  toAnchor?: 'top' | 'right' | 'bottom' | 'left';
  attrs?: Record<string, unknown>;
}

export class CreateNetworkConnectorCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Create Network Connector';
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: CreateNetworkConnectorParams;
  private inner?: CreateEdgeCommand;
  private scenarioEdgeId: ReturnType<typeof createEdgeId>;

  constructor(params: CreateNetworkConnectorParams) {
    this.params = params;
    this.id = params.id ?? `create-network-connector-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.timestamp = Date.now();
    this.description = `Create network connector ${params.type}`;
    this.scenarioEdgeId = createEdgeId(params.id ?? `scenario-edge-${this.id}`);
  }

  execute(): void {
    this.inner = new CreateEdgeCommand({
      id: this.params.id,
      type: this.params.type,
      from: this.params.from,
      to: this.params.to,
      fromAnchor: this.params.fromAnchor,
      toAnchor: this.params.toAnchor,
      category: 'network',
      attrs: this.params.attrs,
    } as any);
    this.inner.execute();
  }

  undo(): void {
    this.inner?.undo?.();
  }

  redo(): void {
    this.inner?.redo?.();
  }

  toScenarioDelta(): CommandDeltaContribution {
    const addition: EdgeAddition = {
      id: this.scenarioEdgeId,
      type: this.params.type,
      from: createNodeId(this.params.from),
      to: createNodeId(this.params.to),
      props: { ...(this.params.attrs ?? {}) },
    };
    return { edgeAdditions: [addition] };
  }

  getUndoDelta(): CommandDeltaContribution {
    return {
      edgeRemovals: [{ edgeId: this.scenarioEdgeId }],
    };
  }
}
